import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useEapiAuth } from "@/hooks/useEapiAuth";
import { cancelOtherActiveLegTrips } from "@/lib/eapi-leg-cancel";
import { isAlreadyCancelledUpstreamError } from "@/lib/eapi-cancel-utils";

export function useMergeOrders() {
  const qc = useQueryClient();
  const { eapiClient, isReady: eapiReady } = useEapiAuth();
  return useMutation({
    mutationFn: async (args: {
      keptId: string;
      mergedId: string;
      overrides: Record<string, string | null>;
    }) => {
      // ── Sync-then-Persist: cancel the merged order's upstream EAPI trips
      // BEFORE the local merge. The merged order ends up canceled, so leaving
      // live upstream trips behind would orphan them.
      const { data: mergedRow, error: rowErr } = await supabase
        .from("orders")
        .select("id, unique_id, eapi_trip_id")
        .eq("id", args.mergedId)
        .maybeSingle<{ id: string; unique_id: string | null; eapi_trip_id: string | null }>();
      if (rowErr) throw rowErr;
      if (!mergedRow) throw new Error("Order to merge was not found");

      if (!eapiReady || !eapiClient) {
        throw new Error(
          "External trip system not ready — cannot merge orders yet. Please retry shortly.",
        );
      }

      const reason = "Order merged into duplicate";

      if (mergedRow.eapi_trip_id) {
        try {
          await eapiClient.cancelTrip(mergedRow.eapi_trip_id, reason);
        } catch (err: any) {
          if (isAlreadyCancelledUpstreamError(err)) {
            console.log(
              `[merge] upstream trip ${mergedRow.eapi_trip_id} already cancelled, proceeding`,
            );
          } else {
            throw new Error(
              `Failed to cancel upstream EAPI trip ${mergedRow.eapi_trip_id}: ${
                err?.message ?? err
              }. Orders were NOT merged.`,
            );
          }
        }
      }

      // Per-leg fan-out for multi-leg orders (parent has no eapi_trip_id).
      await cancelOtherActiveLegTrips(
        args.mergedId,
        mergedRow.eapi_trip_id ?? null,
        eapiClient,
        reason,
        "order_merged",
      );

      const { error } = await supabase.rpc("merge_orders", {
        _kept_id: args.keptId,
        _merged_id: args.mergedId,
        _overrides: args.overrides as any,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Orders merged");
      qc.invalidateQueries({ queryKey: ["duplicate-order-pairs"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (err: Error) => toast.error(err.message || "Failed to merge orders"),
  });
}

export function useDismissDuplicatePair() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { orderAId: string; orderBId: string; note?: string }) => {
      const { error } = await supabase.rpc("dismiss_duplicate_pair", {
        _order_a: args.orderAId,
        _order_b: args.orderBId,
        _note: args.note ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Marked as not duplicates");
      qc.invalidateQueries({ queryKey: ["duplicate-order-pairs"] });
    },
    onError: (err: Error) => toast.error(err.message || "Failed to dismiss"),
  });
}